import React, {ReactNode, useCallback, useState} from 'react';

import debounce from 'lodash/debounce';

import {
  ActionMeta,
  CreatableProps as SelectProps,
  SweetCreatableReactSelect,
  SweetReactSelect,
} from 'app/components/general/SweetReactSelect';
import {SELECT_PAGE_SIZE} from 'app/utils/constants';

import {SelectMenuList} from './SelectMenu';
import {SelectOption} from './SelectOption';
import {SelectMultiValueLabel, SelectSingleValue} from './SelectValue';

import type {ContentFetchProps} from 'app/general/datafetchers/builders';
import type {InfiniteListFetcherProps} from 'app/general/datafetchers/InfiniteListFetcher';

interface ValueWithURL {
  url: string;
}

interface CommonProps<IT, P, V = IT> {
  fetchSelectedParams?: {[param: string]: string | number};
  selected: Array<IT> | IT | null;
  placeholder: string;
  selectLimit?: number;
  selectLimitText?: ReactNode;
  menuIsOpen?: boolean;
  InitialItemsFetcher: React.FC<ContentFetchProps<IT, any>>;
  ItemsInfiniteListFetcher: React.FC<InfiniteListFetcherProps<IT>>;
  onInputChange?: (value: string) => any;
  selectProps?: Partial<SelectProps<IT, V, ReactNode>>;
  components?: {[component: string]: any};
  children?: ReactNode;
  disabled?: boolean;
  lazyFetch?: boolean;
  isRTKFetcher?: boolean;
  params?: P;
  searchOrderingParam?: {ordering: '-rank' | 'name'};
  typeIcon?: null | string | ((value: IT) => string | null);
  translateSearchParams?: (
    param: string,
    opts?: {ordering?: string}
  ) => {[param: string]: string | number};
  orderFunction?: (items: Array<IT>) => Array<IT>;
  addFirstOption?: IT;
  creatable?: boolean;
  noOptionsMessage?: (inputValue: string) => React.ReactNode;
  formatOptionLabel?: (option: IT) => string | React.ReactNode;
  getOptionLabel?: (option: IT) => string;
}

interface SingleItemProps<IT, P> extends CommonProps<IT, P> {
  isMulti: false;
  selected: IT | null;
  onChange: (value: IT, meta: ActionMeta<IT>) => Promise<unknown>;
}

interface MultiItemProps<IT, P> extends CommonProps<IT, P> {
  isMulti: true;
  selected: Array<IT>;
  onChange: (values: Array<IT>, meta: ActionMeta<IT>) => Promise<unknown>;
}

export type SelectBaseProps<T, P> =
  | SingleItemProps<T, P>
  | MultiItemProps<T, P>;

export function SelectBase<
  IT,
  P extends Record<string, any> = Record<string, any>
>({
  selectLimitText,
  selectLimit,
  children,
  selectProps,
  isMulti,
  isRTKFetcher,
  placeholder,
  disabled,
  typeIcon,
  addFirstOption,
  InitialItemsFetcher,
  ItemsInfiniteListFetcher,
  selected,
  onChange,
  onInputChange,
  lazyFetch,
  fetchSelectedParams,
  orderFunction,
  noOptionsMessage,
  formatOptionLabel,
  getOptionLabel,
  menuIsOpen,
  components = {},
  params = {} as P,
  translateSearchParams = (search: string, params?: {ordering?: string}) => ({
    ordering: search === '' ? 'name' : '-rank',
    ...params,
    search,
  }),
  searchOrderingParam = {ordering: '-rank'},
  creatable = false,
}: SelectBaseProps<IT, P>) {
  const [localfetchSelectedParams] = useState(fetchSelectedParams);

  const [loading, setLoading] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [inputText, setInputText] = useState('');
  const [search, setSearch] = useState('');
  const [fetchOnOpen, setFetchOnOpen] = useState(lazyFetch === true);

  const delayedSearch = useCallback(
    debounce((search: string) => {
      setWaiting(false);
      setSearch(search);
    }, 500),
    []
  );

  const onLimit =
    selectLimit != null &&
    selected != null &&
    Array.isArray(selected) &&
    selected.length >= selectLimit;
  let orderedParams = {...params};

  const clearLoading = (value: any) => {
    setLoading(false);
    return value;
  };

  const handleOnChange = (value: any, action: ActionMeta<IT>) => {
    setLoading(true);
    return Promise.resolve(onChange(value || (isMulti ? [] : null), action))
      .then(clearLoading)
      .catch(clearLoading);
  };

  const handleCreateOption = (value: any) => {
    setLoading(true);
    return Promise.resolve(
      selectProps != null && selectProps.onCreateOption != null
        ? selectProps.onCreateOption(value)
        : null
    )
      .then(clearLoading)
      .catch(clearLoading);
  };

  const handleInputChange = (newText: string) => {
    if (newText !== inputText) {
      setInputText(newText);
      setWaiting(true);
      if (onInputChange != null) {
        onInputChange(newText);
      }
      delayedSearch(newText);
    }
    return newText;
  };

  const maybeFetch = (fetchFunc: () => any) => {
    if (fetchOnOpen) {
      fetchFunc();
      setFetchOnOpen(false);
    }
  };

  const wrapIfRequired = (
    content: JSX.Element,
    children: ReactNode | void,
    onLimit: boolean,
    selectLimitText: ReactNode | void
  ): JSX.Element => {
    if (children != null || onLimit) {
      return (
        <div>
          {content}
          {children}
          {onLimit ? (
            <div className="small form-group mt-2">
              {selectLimitText ?? null}
            </div>
          ) : null}
        </div>
      );
    }
    return content;
  };

  const mergedSelectProps = {
    getNewOptionData: (title: string) => title,
    ...selectProps,
    onCreateOption: handleCreateOption,
  };

  const SelectComponent = creatable
    ? SweetCreatableReactSelect
    : SweetReactSelect;
  const skipInitialFetch = localfetchSelectedParams == null;

  if (search !== '' && params != null && params.ordering != null) {
    orderedParams = {...params, ...searchOrderingParam};
  }

  return (
    <InitialItemsFetcher
      params={{...localfetchSelectedParams, page_size: 500}}
      skipInitialFetch={skipInitialFetch}
    >
      {(response) =>
        wrapIfRequired(
          <ItemsInfiniteListFetcher
            fetchParams={{
              ...orderedParams,
              ...translateSearchParams(search, orderedParams),
              page_size: SELECT_PAGE_SIZE,
            }}
            skipInitialFetch={fetchOnOpen}
          >
            {({
              allResults,
              hasNext,
              loadNext,
              loading: localIsLoading,
              loadingLast,
              refetchData,
            }) => {
              if (orderFunction) {
                allResults = orderFunction(allResults);
              }

              const options: Array<IT> = onLimit
                ? []
                : addFirstOption != null && allResults.length
                ? [addFirstOption as IT, ...allResults]
                : allResults;

              const isLoading =
                waiting ||
                loading ||
                (localIsLoading && !fetchOnOpen) ||
                (loadingLast && isRTKFetcher) || // isMulti uses new infinite fetcher that behaves differently.
                (response.loading && !skipInitialFetch);

              return (
                <SelectComponent<IT>
                  onMenuOpen={() => maybeFetch(refetchData)}
                  isClearable={true}
                  isDisabled={disabled}
                  isMulti={isMulti}
                  isLoading={isLoading}
                  getOptionValue={(obj) => (obj as any).url}
                  getOptionLabel={
                    getOptionLabel || ((obj) => (obj as any).name!)
                  }
                  formatOptionLabel={formatOptionLabel}
                  components={{
                    MenuList: SelectMenuList,
                    Option: SelectOption,
                    MultiValueLabel: SelectMultiValueLabel,
                    SingleValue: SelectSingleValue,
                    ...components,
                  }}
                  filterOption={() => true}
                  placeholder={placeholder}
                  value={selected}
                  onChange={handleOnChange}
                  onInputChange={handleInputChange}
                  options={options}
                  menuIsOpen={waiting && !options.length ? false : menuIsOpen}
                  noOptionsMessage={noOptionsMessage}
                  {...mergedSelectProps}
                  // extra props passed on to compnents in selectProps
                  extraProps={{
                    inputText,
                    hasNext,
                    loadNext,
                    waiting,
                    loadingLast,
                    onLimit,
                    selectLimitText,
                    typeIcon,
                    ...(selectProps?.extraProps ?? {}),
                  }}
                />
              );
            }}
          </ItemsInfiniteListFetcher>,
          children,
          onLimit,
          selectLimitText
        )
      }
    </InitialItemsFetcher>
  );
}

type SingleManualProps<IT> = {
  disabled?: boolean;
  isMulti: false;
  placeholder: string;
  selected: Array<IT>;
  options: Array<IT>;
  onChange: (changed: IT, action: ActionMeta<IT>) => Promise<any>;
  menuIsOpen?: boolean;
  selectProps?: {[prop: string]: any};
  typeIcon?: null | string | ((value: IT) => string | null);
  noOptionsMessage?: (inputValue: string) => React.ReactNode;
  formatOptionLabel?: (option: IT) => string | React.ReactNode;
  getOptionLabel?: (option: IT) => string;
} & (IT extends ValueWithURL
  ? {getOptionValue?: (obj: IT) => string | number}
  : {getOptionValue: (obj: IT) => string | number});

export function ManualSelectBase<IT>(props: SingleManualProps<IT>) {
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const selectProps = props.selectProps || {};

  return (
    <SweetReactSelect
      className={selectProps.className}
      isClearable={false}
      isDisabled={props.disabled}
      isMulti={props.isMulti}
      isLoading={isLoading}
      options={props.options}
      getOptionValue={props.getOptionValue || ((obj) => (obj as any).url)}
      getOptionLabel={props.getOptionLabel || ((obj) => (obj as any).name!)}
      formatOptionLabel={props.formatOptionLabel}
      components={{
        MenuList: SelectMenuList,
        Option: SelectOption,
        MultiValueLabel: SelectMultiValueLabel,
        SingleValue: SelectSingleValue,
      }}
      filterOption={() => true}
      placeholder={props.placeholder}
      value={props.selected}
      onChange={(value: any, action: ActionMeta<IT>) => {
        setLoading(true);
        props.onChange(value || [], action).then(() => setLoading(false));
      }}
      menuIsOpen={props.menuIsOpen}
      noOptionsMessage={props.noOptionsMessage}
      // extra props passed on to compnents in selectProps
      extraProps={{typeIcon: props.typeIcon ?? null}}
    />
  );
}
